import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import * as Sentry from "@sentry/browser";

import LoadingScreen from "App/common/LoadingScreen/LoadingScreen";
import { useAuth } from "App/components/Auth";
import { highnote } from "@highnote/server/src/sdk";
import { useToast } from "App/common/useToast";
import { routePaths } from "App/modules/urls";

export const Submit = () => {
  const history = useHistory();
  const { authLoading, user } = useAuth();
  const { addErrorMessage } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const typeformFormId = params.get("typeformFormId");

  // If not authenticated, redirect to /login with referrer params as top-level
  // query params so Login.tsx can pass them through to authenticateUser as signUpMetadata.
  useEffect(() => {
    if (authLoading || user) return;

    const loginParams = new URLSearchParams();
    if (typeformFormId) {
      loginParams.set("referrer_type", "form_submission");
      loginParams.set("referrer_id", typeformFormId);
    }
    loginParams.set(
      "returnTo",
      `${routePaths.submit}?typeformFormId=${typeformFormId}`,
    );
    history.replace(`/login?${loginParams.toString()}`);
  }, [authLoading, user, typeformFormId]);

  useEffect(() => {
    if (authLoading || !user || isProcessing) {
      return;
    }

    if (!typeformFormId) {
      addErrorMessage("Missing typeform form ID");
      history.replace(routePaths.library);
      return;
    }

    const handleTypeformRedirect = async () => {
      setIsProcessing(true);

      try {
        const { submissionToken, email, name, firstName, lastName } =
          await highnote.getOrCreateSubmissionToken();

        console.log("Got submission token and user info", {
          submissionToken,
          email,
          name,
          firstName,
          lastName,
        });

        // Build Typeform URL with pre-filled fields
        // Per https://help.typeform.com/hc/en-us/articles/360052676612
        const typeformBaseUrl = `https://form.typeform.com/to/${typeformFormId}`;
        const prefillParams = new URLSearchParams();

        if (submissionToken) {
          prefillParams.set("submission_token", submissionToken);
        }
        if (email) {
          prefillParams.set("auth_email", email);
        }
        if (firstName) {
          prefillParams.set("auth_first_name", firstName);
        }
        if (lastName) {
          prefillParams.set("auth_last_name", lastName);
        }
        if (name) {
          prefillParams.set("artistName", name);
        }

        const allAuthFieldsSet = !!(email && firstName && lastName);
        prefillParams.set("all_auth_fields_set", String(allAuthFieldsSet));

        // Redirect to Typeform with pre-filled fields via URL fragment
        const typeformUrl = `${typeformBaseUrl}#${prefillParams.toString()}`;
        window.location.href = typeformUrl;
      } catch (err) {
        Sentry.captureException(err);
        addErrorMessage(`Failed to process submission: ${err.message || err}`);
        history.replace(routePaths.library);
      }
    };

    handleTypeformRedirect();
  }, [authLoading, user, isProcessing, typeformFormId]);

  return <LoadingScreen />;
};
